/**
 * 変換状況の表示ロジック。
 *
 * 表示の判断を純粋関数に切り出しているのは、ここを間違えると
 * 「動いていないのか、順番待ちなのか、失敗したのか」が利用者に伝わらないため。
 * この画面の価値はまさにそこにあるので、テストで固定しておく。
 */

export type TranscodeJob = {
  assetId: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  attempt: number
  /** 実行中の細かい段階。コンテナに聞けなかった場合は null。 */
  phase: 'downloading' | 'probing' | 'transcoding' | 'uploading' | 'done' | 'failed' | null
  /** 0-100。算出できない段階では null。 */
  percent: number | null
  /** 待機中の順番（1始まり）。 */
  queuePosition: number | null
  startedAt: string | null
  finishedAt: string | null
  elapsedMs: number | null
  errorCode: string | null
  errorMessage: string | null
  /** 掴まれたまま放置されている（実行主体が落ちた可能性が高い）。 */
  stale?: boolean
}

export type StatusDisplay = {
  /** 画面に出す一行。 */
  label: string
  /** 進捗バーの塗り（0-100）。null なら不定（アニメーションで表現する）。 */
  percent: number | null
  /** 見た目の系統。 */
  tone: 'idle' | 'waiting' | 'active' | 'done' | 'error'
  /** バーそのものを出すか。 */
  showBar: boolean
}

const PHASE_LABEL: Record<string, string> = {
  downloading: 'Box から取得中',
  probing: '解析中',
  transcoding: '解像度変換・透かし処理中',
  uploading: '保存中',
  done: '仕上げ中',
  failed: '失敗',
}

/** 経過時間を「1分20秒」のような日本語にする。 */
export function formatElapsed(ms: number | null): string | null {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return null
  const total = Math.floor(ms / 1000)
  const min = Math.floor(total / 60)
  const sec = total % 60
  return min > 0 ? `${min}分${sec}秒` : `${sec}秒`
}

/**
 * 素材1件の表示を決める。
 *
 * @param job          その素材の変換ジョブ（無ければ null）
 * @param hasProxy     すでにプロキシがあるか（assets.proxy_r2_key）
 */
export function describeTranscode(job: TranscodeJob | null, hasProxy: boolean): StatusDisplay {
  // 完成しているものに進捗を出す意味は無い。
  // ジョブ行が succeeded で残っていても、実体があるなら「完了」で十分。
  if (hasProxy && (!job || job.status === 'succeeded')) {
    return { label: '再生可能', percent: 100, tone: 'done', showBar: false }
  }

  if (!job) {
    return { label: '未変換', percent: null, tone: 'idle', showBar: false }
  }

  if (job.status === 'failed') {
    return {
      label: `失敗（${job.errorCode ?? '原因不明'}）`,
      percent: null,
      tone: 'error',
      showBar: false,
    }
  }

  if (job.status === 'queued') {
    // 「止まっている」のか「順番待ち」なのかが分からないのが一番不安なので、
    // 分かるときは待ち順を必ず添える。
    return {
      label: job.queuePosition ? `待機中（${job.queuePosition}番目）` : '待機中',
      percent: null,
      tone: 'waiting',
      showBar: true,
    }
  }

  // コンテナが失敗を報告している間、DB はまだ running のことがある（書き戻し前）。
  // ここを running として扱うと「失敗 47%」が進行中の色と動くバーで出て、
  // 失敗したのか動いているのか読み取れなくなる。失敗を優先して判定する。
  if (job.status === 'running' && job.phase === 'failed') {
    return {
      label: '失敗（詳細を確認中）',
      percent: null,
      tone: 'error',
      showBar: false,
    }
  }

  if (job.status === 'running' && job.stale) {
    // 進行中と言い切ってはいけない。実行主体が落ちていても running のまま残るため、
    // 「30分経過」を進捗のように見せると、いつまでも待たせてしまう。
    const elapsed = formatElapsed(job.elapsedMs)
    return {
      label: `応答がありません${elapsed ? `（${elapsed}経過）` : ''}。再実行してください`,
      percent: null,
      tone: 'error',
      showBar: false,
    }
  }

  if (job.status === 'running') {
    const phase = job.phase ? (PHASE_LABEL[job.phase] ?? '処理中') : '処理中'
    const elapsed = formatElapsed(job.elapsedMs)
    const retry = job.attempt > 1 ? `／${job.attempt}回目` : ''
    const suffix = elapsed ? `（${elapsed}経過${retry}）` : retry ? `（${retry.slice(1)}）` : ''
    return {
      label: job.percent !== null ? `${phase} ${job.percent}%${suffix}` : `${phase}${suffix}`,
      // 0% を幅0のバーで出すと「止まっている」ようにしか見えない。
      // 動き出す前は不定表示に倒す。
      percent: job.percent === null || job.percent === 0 ? null : job.percent,
      tone: 'active',
      showBar: true,
    }
  }

  // succeeded なのに proxy_r2_key が無い＝素材一覧の取得が古い。
  // 「完了」と言い切らず、反映待ちであることを示す。
  return { label: '完了（反映待ち）', percent: 100, tone: 'done', showBar: false }
}

/**
 * 1件でも動いているならポーリングを続ける。
 *
 * **stale な running は「動いている」に数えない。** 数えると、実行主体が落ちて
 * 放置された行のせいで3秒ポーリングが永久に止まらなくなる。
 * 失敗を報告済みのものも同様。
 */
export function hasActiveJobs(jobs: TranscodeJob[]): boolean {
  return jobs.some(
    (j) =>
      j.status === 'queued' ||
      (j.status === 'running' && !j.stale && j.phase !== 'failed'),
  )
}
